/* eslint-disable max-lines-per-function */
import { AppointmentDetails } from '@dreryk/egabinet-appointment-frontend-api';
import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { AppointmentAdvancePaymentRefund } from '@dreryk/egabinet-appointment-advance-payment-frontend-api';

import { Notification, NotificationSize, NotificationType, StyledFlex } from '@dreryk/react-components';

import { appointmentAdvancePaymentAutomaticRefundVisitor } from '~libs.shared.appointment-advance-payment';
import {
   AppointmentBeginDateTextView,
   AppointmentPatientTextView,
   AppointmentPractitionerTextView,
} from '~libs.desktop.shared.appointment';

import { AdvancePaymentDateTextView } from '../components/AdvancePaymentDateTextView';
import { AdvancePaymentMethodTypeTextView } from '../components/AdvancePaymentMethodTypeTextView';
import { AdvancePaymentRefundAmountTextView } from '../components/AdvancePaymentRefundAmountTextView';
import { AdvancePaymentRefundMethodTypeTextView } from '../components/AdvancePaymentRefundMethodTypeTextView';
import { isAutomaticRefundValid } from '../shared/automaticRefund';
import { translationKeys } from '../translations';
import { AdvancePaymentAmountTextView } from '../components/AdvancePaymentAmountTextView';

type AppointmentAdvancePaymentPaidViewProps = Readonly<{
   automaticRefundEnabled: boolean;
   appointmentDetails: AppointmentDetails;
   advancePayment: FinancialSettlementAdvancePayment;
   appointmentAdvancePaymentRefund: AppointmentAdvancePaymentRefund | null;
}>;

export const AppointmentAdvancePaymentPaidView = (props: AppointmentAdvancePaymentPaidViewProps): JSX.Element => {
   return (
      <>
         <StyledFlex gap={20}>
            <AppointmentBeginDateTextView beginDate={props.appointmentDetails.beginDate} />
            <AppointmentPatientTextView patient={props.appointmentDetails.patient} />
         </StyledFlex>

         <AppointmentPractitionerTextView practitioner={props.appointmentDetails.practitioner} />

         <AdvancePaymentDateTextView paymentDate={props.advancePayment.paymentDate} />

         <StyledFlex direction={'column'} gap={20}>
            <StyledFlex gap={20}>
               <StyledFlex gap={20} direction={'column'}>
                  <AdvancePaymentMethodTypeTextView
                     paymentMethodType={props.advancePayment.paymentMethod?.methodType}
                  />
                  <AdvancePaymentAmountTextView amount={props.advancePayment.remainingAmount} />
               </StyledFlex>

               {appointmentAdvancePaymentAutomaticRefundVisitor(
                  {
                     automaticRefundEnabled: props.automaticRefundEnabled,
                     appointmentAdvancePaymentRefund: props.appointmentAdvancePaymentRefund,
                  },
                  {
                     visitWithAutomaticRefund: ({ refundExpirationDate, refundMethodType, amount }) => (
                        <StyledFlex gap={20} direction={'column'}>
                           <AdvancePaymentRefundMethodTypeTextView refundMethodType={refundMethodType} />
                           <AdvancePaymentRefundAmountTextView
                              refundAmount={amount}
                              expirationDate={refundExpirationDate}
                           />
                        </StyledFlex>
                     ),
                  }
               )}
            </StyledFlex>
         </StyledFlex>

         {appointmentAdvancePaymentAutomaticRefundVisitor(
            {
               automaticRefundEnabled: props.automaticRefundEnabled,
               appointmentAdvancePaymentRefund: props.appointmentAdvancePaymentRefund,
            },
            {
               visitWithAutomaticRefund: () => (
                  <Notification
                     testId={'automaticAdvancePaymentRefundsDetails'}
                     type={NotificationType.INFO}
                     size={NotificationSize.SMALLER}
                     label={
                        isAutomaticRefundValid(props.appointmentAdvancePaymentRefund?.refundExpirationDate)
                           ? translationKeys.automaticAdvancePaymentRefundsDetails
                           : translationKeys.automaticAdvancePaymentRefundsExpiredDetails
                     }
                  />
               ),
               visitWithoutAutomaticRefund: () => null,
            }
         )}
      </>
   );
};
